import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import classNames from 'classnames';
import { FiEye, FiEdit2, FiTrash2 } from 'react-icons/fi';
import {
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_PUBLISHED,
  LISTING_STATE_CLOSED,
  LISTING_STATE_DRAFT,
  propTypes,
} from '../../util/types';
import { ensureOwnListing } from '../../util/data';
import {
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
} from '../../util/urlHelpers';
import { NamedLink, ToggleSwitch } from '../../components';

import css from './ManageProjectListingCard.css';

// Build NamedLink props for viewing the listing. Draft / pending listings are
// only viewable through the variant route.
const viewLinkProps = (id, slug, state) => {
  const isPendingApproval = state === LISTING_STATE_PENDING_APPROVAL;
  const isDraft = state === LISTING_STATE_DRAFT;
  const variant = isDraft
    ? LISTING_PAGE_DRAFT_VARIANT
    : isPendingApproval
    ? LISTING_PAGE_PENDING_APPROVAL_VARIANT
    : null;

  return isPendingApproval || isDraft
    ? { name: 'ProjectListingPageVariant', params: { id, slug, variant } }
    : { name: 'ProjectListingPage', params: { id, slug } };
};

// A draft reopens where the user left off: the chat until the writer has
// produced a draft (or the user saved one), then the draft editor. A manual-fill
// draft had no chat, so it always opens in the editor.
const draftWizardTab = listing => {
  const { publicData, privateData } = listing.attributes;
  const hasDraftText = !!(
    (publicData && publicData.descriptionText) ||
    (privateData && privateData.agentListingDraft)
  );
  const isManualFill = !!(
    privateData &&
    privateData.jobPostingChat &&
    privateData.jobPostingChat.manualFill
  );
  return hasDraftText || isManualFill ? 'draft' : 'chat';
};

// A second click this soon after arming is treated as an accidental double
// click and ignored.
export const DISCARD_CONFIRM_DELAY_MS = 400;
// An armed button disarms itself after this long. Keep in sync with the
// `discardCountdown` animation duration in the CSS.
export const DISCARD_ARMED_TIMEOUT_MS = 4000;

// Two-click delete: the first click arms the button (it widens into a red
// "Delete" with a countdown bar), the second click deletes. A click elsewhere,
// Escape or the countdown running out disarms it.
const DiscardDraftButton = props => {
  const { intl, armed, disabled, onArm, onDisarm, onConfirm } = props;
  const buttonRef = useRef(null);
  const armedAtRef = useRef(0);

  useEffect(() => {
    if (!armed) {
      return undefined;
    }
    const timeout = window.setTimeout(onDisarm, DISCARD_ARMED_TIMEOUT_MS);
    const disarmOnOutsideClick = e => {
      if (buttonRef.current && !buttonRef.current.contains(e.target)) {
        onDisarm();
      }
    };
    const disarmOnEscape = e => {
      if (e.key === 'Escape') {
        onDisarm();
      }
    };
    document.addEventListener('mousedown', disarmOnOutsideClick);
    document.addEventListener('keydown', disarmOnEscape);
    return () => {
      window.clearTimeout(timeout);
      document.removeEventListener('mousedown', disarmOnOutsideClick);
      document.removeEventListener('keydown', disarmOnEscape);
    };
  }, [armed, onDisarm]);

  const handleClick = () => {
    if (!armed) {
      armedAtRef.current = Date.now();
      onArm();
    } else if (Date.now() - armedAtRef.current >= DISCARD_CONFIRM_DELAY_MS) {
      onConfirm();
    }
  };

  const label = intl.formatMessage({ id: 'ManageProjectListingCard.discardDraft' });
  const confirmLabel = intl.formatMessage({ id: 'ManageProjectListingCard.discardDraftConfirm' });

  return (
    <button
      ref={buttonRef}
      type="button"
      className={classNames(css.actionButton, css.discardButton, {
        [css.discardButtonArmed]: armed,
      })}
      onClick={handleClick}
      disabled={disabled}
      title={label}
      aria-label={armed ? confirmLabel : label}
    >
      <FiTrash2 className={css.discardIcon} />
      <span className={css.discardLabel} aria-hidden="true">
        <span className={css.discardLabelText}>{confirmLabel}</span>
      </span>
      {armed ? <span className={css.discardCountdown} aria-hidden="true" /> : null}
    </button>
  );
};

export const ManageProjectListingCardComponent = props => {
  const {
    className,
    rootClassName,
    hasClosingError,
    hasOpeningError,
    hasDiscardError,
    intl,
    actionsInProgressListingId,
    listing,
    onCloseListing,
    onOpenListing,
    onDiscardDraft,
  } = props;
  const [discardArmed, setDiscardArmed] = useState(false);
  const disarmDiscard = useCallback(() => setDiscardArmed(false), []);
  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureOwnListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', state } = currentListing.attributes;
  const slug = createSlug(title);

  const isPublished = state === LISTING_STATE_PUBLISHED;
  const isClosed = state === LISTING_STATE_CLOSED;
  const isDraft = state === LISTING_STATE_DRAFT;
  const isPendingApproval = state === LISTING_STATE_PENDING_APPROVAL;

  const thisListingInProgress =
    actionsInProgressListingId && actionsInProgressListingId.uuid === id;
  const hasError = hasOpeningError || hasClosingError || hasDiscardError;

  const statusKey = isDraft
    ? 'ManageProjectListingCard.statusDraft'
    : isPendingApproval
    ? 'ManageProjectListingCard.statusPending'
    : isClosed
    ? 'ManageProjectListingCard.statusClosed'
    : 'ManageProjectListingCard.statusActive';

  const toggleDisabled = isDraft || isPendingApproval || !!thisListingInProgress;

  const handleToggle = next => {
    if (actionsInProgressListingId) {
      return;
    }
    if (next) {
      onOpenListing(currentListing.id);
    } else {
      onCloseListing(currentListing.id);
    }
  };

  const editListingLinkType = isDraft
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;

  const viewLink = viewLinkProps(id, slug, state);
  const editLinkParams = {
    id,
    slug,
    type: editListingLinkType,
    tab: isDraft ? draftWizardTab(currentListing) : 'draft',
  };
  // Drafts aren't finished listings yet, so the card opens them for editing.
  const titleLink = isDraft ? { name: 'EditProjectListingPage', params: editLinkParams } : viewLink;
  const viewLabel = intl.formatMessage({ id: 'ManageProjectListingCard.viewListing' });
  const editLabel = intl.formatMessage({ id: 'ManageProjectListingCard.editListing' });
  // Only drafts (never published) can be discarded; everything else gets closed instead.
  const canDiscard = isDraft && !!onDiscardDraft;
  // While the delete is armed, view and edit slide away so the widened button
  // fits even the narrowest cards.
  const secondaryActionClasses = classNames(css.actionButton, css.collapsibleAction, {
    [css.collapsibleActionHidden]: canDiscard && discardArmed,
  });

  return (
    <div className={classes}>
      <NamedLink className={css.titleLink} name={titleLink.name} params={titleLink.params}>
        <h2 className={css.title}>{title}</h2>
      </NamedLink>

      <div className={css.divider} />

      <div className={css.footer}>
        <div className={css.statusWrapper}>
          <ToggleSwitch
            className={css.toggle}
            isOn={isPublished}
            disabled={toggleDisabled}
            onToggle={handleToggle}
          />
          <span
            className={classNames(css.statusLabel, {
              [css.statusInactive]: !isPublished,
              [css.statusLabelDraft]: isDraft,
            })}
          >
            <FormattedMessage id={statusKey} />
          </span>
        </div>

        <div className={css.actions}>
          <NamedLink
            className={secondaryActionClasses}
            name={viewLink.name}
            params={viewLink.params}
            title={viewLabel}
            aria-label={viewLabel}
          >
            <FiEye />
          </NamedLink>
          <NamedLink
            className={secondaryActionClasses}
            name="EditProjectListingPage"
            params={editLinkParams}
            title={editLabel}
            aria-label={editLabel}
          >
            <FiEdit2 />
          </NamedLink>
          {canDiscard ? (
            <DiscardDraftButton
              intl={intl}
              armed={discardArmed}
              disabled={!!thisListingInProgress}
              onArm={() => setDiscardArmed(true)}
              onDisarm={disarmDiscard}
              onConfirm={() => onDiscardDraft(currentListing.id)}
            />
          ) : null}
        </div>
      </div>

      {hasError ? (
        <p className={css.error}>
          <FormattedMessage id="ManageProjectListingCard.actionFailed" />
        </p>
      ) : null}
    </div>
  );
};

ManageProjectListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  actionsInProgressListingId: null,
  hasDiscardError: false,
  onDiscardDraft: null,
};

const { bool, func, shape, string } = PropTypes;

ManageProjectListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  hasClosingError: bool.isRequired,
  hasOpeningError: bool.isRequired,
  hasDiscardError: bool,
  intl: intlShape.isRequired,
  listing: propTypes.ownListing.isRequired,
  actionsInProgressListingId: shape({ uuid: string.isRequired }),
  onCloseListing: func.isRequired,
  onOpenListing: func.isRequired,
  onDiscardDraft: func,
};

DiscardDraftButton.propTypes = {
  intl: intlShape.isRequired,
  armed: bool.isRequired,
  disabled: bool.isRequired,
  onArm: func.isRequired,
  onDisarm: func.isRequired,
  onConfirm: func.isRequired,
};

export default compose(injectIntl)(ManageProjectListingCardComponent);
